import { type ReactNode, StrictMode } from 'react';
import clsx from 'clsx';
import { Text } from '@hungryroot/ramen';
import { AuthState, type CustomerAuthState } from '@hungryroot/rest-hooks';
import { buttonClickedEvent } from '@hungryroot/segment';
import { AppleAppStoreBadge } from './AppleAppStoreBadge';
import {
  useAnonymousFooterTopLinks,
  type ILink,
} from './hooks/useAnonymousFooterTopLinks';
import styles from './hungryroot-footer.module.css';

const authenticatedTopLinks: ILink[] = [
  { path: '/recipes/', text: 'The Cookbook', external: true },
  { path: '/preferences/', text: 'Food Profile', external: true },
  { path: '/account/referral/', text: 'Referral', external: true },
  { path: '/gift/', text: 'Gifts', external: true },
];

const infoLinks: ILink[] = [
  { path: '/leadership/', text: 'Leadership', external: true },
  { path: '/philosophy/', text: 'Philosophy', external: true },
  { path: '/packaging/', text: 'Sustainability', external: true },
  { path: '/careers/', text: 'Careers', external: true },
  {
    path: 'https://support.hungryroot.com/hc/en-us',
    text: 'Contact us',
    external: true,
    target: '_blank',
    rel: 'noreferrer',
  },
  { path: '/faq/', text: 'FAQ', target: '_blank' },
];

const communityLinks: ILink[] = [
  {
    path: 'https://blog.hungryroot.com',
    text: 'Blog',
    external: true,
    target: '_blank',
    rel: 'noreferrer',
  },
  {
    path: 'https://www.instagram.com/hungryroot',
    text: 'Instagram',
    external: true,
    target: '_blank',
    rel: 'noreferrer',
  },
  {
    path: 'https://www.tiktok.com/@hungryroot?lang=en',
    text: 'TikTok',
    external: true,
    target: '_blank',
    rel: 'noreferrer',
  },
  {
    path: 'https://www.facebook.com/groups/hungryroot',
    text: 'Facebook',
    external: true,
    target: '_blank',
    rel: 'noreferrer',
  },
];

const bottomLinks: ILink[] = [
  { path: '/terms/', text: 'Terms', external: true, target: '_blank' },
  { path: '/privacy/', text: 'Privacy', external: true, target: '_blank' },
  {
    path: '/accessibility/',
    text: 'Accessibility',
    external: true,
    target: '_blank',
  },
];

const doNotSellLink: ILink = {
  path: 'https://airtable.com/shr3O0BHKqYS0uu5v',
  text: 'Do not sell or share my personal information',
  external: true,
  target: '_blank',
  rel: 'noreferrer',
};

const getLinks = ({
  links,
  className,
  onSendTrackingEvent,
}: {
  links: ILink[];
  className?: string;
  onSendTrackingEvent?: (text: string) => void;
}) => {
  return links.map((link) => {
    const text = (
      <Text className={`${styles.link} ${className}`} weight="normal">
        {link.text}
      </Text>
    );
    return (
      <div className={styles.row} key={link.text}>
        {link.external && (
          <a
            href={link.path}
            data-testid={`footer-${link.text}`}
            target={link.target}
            rel={link.rel}
            onClick={() => {
              onSendTrackingEvent?.(link.text);
            }}
          >
            {text}
          </a>
        )}
        {!link.external && (
          <a
            href={link.path}
            data-testid={`footer-${link.text}`}
            target={link.target}
            rel={link.rel}
          >
            {text}
          </a>
        )}
      </div>
    );
  });
};

interface LogoProps {
  src: string;
  height?: number;
  width?: number;
}

function HungryrootFooterLogo({ src, height, width }: LogoProps) {
  return (
    <img
      data-testid="footer-logo"
      height={height}
      width={width}
      className={styles.logo}
      alt="Hungryroot logo"
      src={src}
      loading="lazy"
      decoding="async"
    />
  );
}

export enum AppBadgeVariant {
  Apple,
  Google,
}

interface BadgeProps {
  variant: AppBadgeVariant;
  src?: string;
  alt?: string;
}

function HungryrootFooterAppBadge({ variant, src, alt }: BadgeProps) {
  switch (variant) {
    case AppBadgeVariant.Apple:
      return <AppleAppStoreBadge className={styles['app-banner']} />;
    case AppBadgeVariant.Google:
      return (
        <img
          className={styles['app-banner']}
          alt={alt}
          src={src}
          loading="lazy"
          decoding="async"
        />
      );
    default:
      return null;
  }
}

function HungryrootCustomerLinks({
  customerAuthState,
}: {
  customerAuthState: Pick<CustomerAuthState, 'authState'>;
}) {
  const { authState } = customerAuthState;

  const anonymousTopLinks = useAnonymousFooterTopLinks();

  if (authState === AuthState.isAuthenticated) {
    return <>{getLinks({ links: authenticatedTopLinks })}</>;
  }

  return (
    <>
      {getLinks({
        links: anonymousTopLinks,
        onSendTrackingEvent: (text) => {
          const buttonName = `${text.toLowerCase()}_footer`;
          buttonClickedEvent({
            button_name: buttonName,
            order_id: null,
            page_position: '',
          });
        },
      })}
    </>
  );
}

interface Props {
  mhrImage: ReactNode;
  customerLinks: ReactNode;
  appleAppStoreBadge?: ReactNode;
  googlePlayBadge?: ReactNode;
  showBorder?: boolean;
}

export function HungryrootFooter({
  mhrImage,
  customerLinks,
  googlePlayBadge,
  appleAppStoreBadge,
  showBorder = true,
}: Props) {
  const copywrite = `© ${new Date().getFullYear()}, Hungryroot. All rights reserved`;

  const containerClass = clsx({
    [styles.footer]: !showBorder,
    [styles['footer-border']]: showBorder,
  });
  return (
    <StrictMode>
      <footer className={containerClass}>
        <div className={styles['container-top']}>
          <div className={styles.section}>
            <div className={styles['primary-nav']}>
              {mhrImage}
              {customerLinks}
            </div>

            {appleAppStoreBadge && googlePlayBadge && (
              <div className={styles['app-banners']}>
                {appleAppStoreBadge}
                {googlePlayBadge}
              </div>
            )}
          </div>
          <div className={styles['secondary-nav']}>
            <div className={styles.section}>
              <Text className={styles['section-heading']} weight="heavy">
                Info
              </Text>
              {getLinks({ links: infoLinks, className: styles.small })}
            </div>
            <div className={styles.section}>
              <Text className={styles['section-heading']} weight="heavy">
                Community
              </Text>
              {getLinks({ links: communityLinks, className: styles.small })}
            </div>
          </div>
        </div>
        <div className={styles['container-bottom']}>
          <div className={styles['container-bottom-inner']}>
            <div className={styles.terms}>
              {getLinks({ links: bottomLinks })}
              <div className={styles.right}>
                {getLinks({ links: [doNotSellLink] })}
              </div>
            </div>
            <div className={styles.copywrite}>
              <Text className={styles['copywrite-text']}>{copywrite}</Text>
            </div>
          </div>
        </div>
      </footer>
    </StrictMode>
  );
}

HungryrootFooter.Logo = HungryrootFooterLogo;
HungryrootFooter.AppBadge = HungryrootFooterAppBadge;
HungryrootFooter.CustomerLinks = HungryrootCustomerLinks;
